// Blob URLs are held by the browser until revoked (or the document is closed), so every
// URL created with URL.createObjectURL must be released explicitly or memory grows
// unbounded in long sessions.

// Grace period so an <img> that is still loading a URL is not broken by an eviction.
const REVOKE_DELAY_MS = 5000;

let liveBlobUrls = 0;

/** Number of blob URLs created through this module and not yet revoked (diagnostics/tests). */
export const getLiveBlobUrlCount = () => liveBlobUrls;

export function createBlobUrl(blob: Blob): string {
  liveBlobUrls += 1;
  return URL.createObjectURL(blob);
}

export function revokeBlobUrl(url: string | undefined | null) {
  if (!url || !url.startsWith('blob:')) return;
  liveBlobUrls -= 1;
  URL.revokeObjectURL(url);
}

/** Revokes after a short delay; use when the URL may still be in use (cache eviction). */
export function revokeBlobUrlLater(url: string | undefined | null) {
  if (!url || !url.startsWith('blob:')) return;
  setTimeout(() => revokeBlobUrl(url), REVOKE_DELAY_MS);
}

/**
 * Assigns a blob URL to an image that is NOT kept in a cache and revokes it as soon
 * as the image has loaded (or failed), since nothing else will reuse the URL.
 */
export function setUncachedBlobSrc(image: HTMLImageElement, blob: Blob): string {
  const url = createBlobUrl(blob);
  const release = () => revokeBlobUrl(url);
  image.addEventListener('load', release, { once: true });
  image.addEventListener('error', release, { once: true });
  // eslint-disable-next-line no-param-reassign
  image.src = url;
  return url;
}
